// src/index.tsx

import   React, { useEffect }   from 'react';
import   ReactDOM               from 'react-dom/client';
import { Provider, useSelector }  from 'react-redux';
import { createBrowserRouter , 
         RouterProvider      }  from 'react-router-dom';
import { store               }  from './store/store';
import { MediaStreamProvider }  from './contexts/MediaStreamContext';
import { SourceRegistryProvider } from './contexts/liveStreamManagment/SourceRegistryContext';
import { NotFound            }  from './components/page/errors/not_found';
import { Login               }  from './components/page/login/login';
import { Version             }  from './components/page/version/version';
import { Desktop             }  from './components/page/desktop/desktop';
import { UserInformations    }  from './components/block/userManagment/UserInformations';
import { PaymentSuccess      }  from './components/page/paymentResult/payment_success';
import { PaymentFailure      }  from './components/page/paymentResult/payment_failure';
import { QuotaPurchaseModal  }  from './components/block/purchaseManagment/QuotaPurchaseModal';
import { TierPickerModal     }  from './components/block/purchaseManagment/TierPickerModal';
import { SubscriptionPurchaseModal }  from './components/block/subscriptionManagment/SubscriptionPurchaseModal';

import { RemoteWebRTCDeviceV2 }  from './components/block/liveStreamManagment/RemoteWebRTCDeviceV2';
import { RemoteWebRTCDeviceV3 }  from './components/block/liveStreamManagment/RemoteWebRTCDeviceV3';

import { installLogger, setLoggerIdentity } from './backendServices/debugManagment/remoteLogger';

/*
import { DeckFullscreenPage       } from './components/page/regie/DeckFullscreenPage';
import { MultiviewFullscreenPage  } from './components/page/regie/MultiviewFullscreenPage';
*/
import './index.css';

const router = createBrowserRouter([
  // /webrtc : page courante (V3 — connexion par lien ou par identifiant).
  // /webrtcv2 : version précédente conservée en filet de sécurité.
  {
    path: "/webrtc",
    element: <RemoteWebRTCDeviceV3/>,
  },
  {
    path: "/webrtc/:uuid",
    element: <RemoteWebRTCDeviceV3/>,
  },
  {
    path: "/webrtcv2",
    element: <RemoteWebRTCDeviceV2/>,
  },
  {
    path: "/webrtcv2/:uuid",
    element: <RemoteWebRTCDeviceV2/>,
  },
  {
    path: "/webrtcv3",
    element: <RemoteWebRTCDeviceV3/>,
  },
  {
    path: "/webrtcv3/:uuid",
    element: <RemoteWebRTCDeviceV3/>,
  },
  {
    path: "/",
    element: <Login />,
  },
  {
    path: "/version",
    element: <Version />,
  },
  {
    path: "/bureau",
    element: <Desktop />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
  {
    path: "/ficheClient",
    element: <UserInformations />,
  },
  {
    path: "/confirmation-paiement",
    element: <PaymentSuccess />,
  },
  {
    path: "/echec-paiement",
    element: <PaymentFailure />,
  },
  /*
  // ── Régie : onglets externes ──────────────────────────────────────────────
  {
    path: "/regie/:uuid/deck",
    element: <DeckFullscreenPage />,
  },
  {
    path: "/regie/:uuid/multiview",
    element: <MultiviewFullscreenPage />,
  },*/
], {
  future: {
    v7_skipActionErrorRevalidation: true,
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
  },
});

// ── Logger global — AVANT root.render() ──────────────────────────────────────


const platform = /iPad|iPhone|iPod/.test(navigator.userAgent) ? 'iOS'
  : /Android/.test(navigator.userAgent) ? 'Android' : 'Desktop';

installLogger(platform);
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Nomme le fichier de logs dès qu'on sait QUI est connecté.
 *
 * Sans ça, tous les journaux s'appelaient `app_<plateforme>_unknown` : la
 * mécanique de bascule existait dans le logger depuis toujours, personne ne
 * l'appelait. Deux administrateurs sur la même sortie internet écrivaient donc
 * dans le MÊME fichier, et on ne pouvait pas savoir lequel avait fait quoi.
 *
 * Monté à la racine, sous le Provider : il couvre aussi bien la connexion par le
 * formulaire que la session restaurée au rechargement de la page — les brancher
 * un par un aurait laissé passer la seconde, qui est le cas le plus fréquent.
 */
const LoggerIdentity: React.FC = () => {
  const authentication = useSelector((state: any) => state.authentication);
  const identite = authentication?.admin?.email || authentication?.login || '';

  useEffect(() => {
    if (identite) setLoggerIdentity({ login: identite });
  }, [identite]);

  return null;
};
// ─────────────────────────────────────────────────────────────────────────────


const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <LoggerIdentity />
      <QuotaPurchaseModal />
      <TierPickerModal />
      <SubscriptionPurchaseModal />
      <MediaStreamProvider>
        {/* Le registre des sources vit ICI, au-dessus du routeur — et surtout au-dessus
            de la `key` par chaîne de BroadcastingChannelList, qui remonte toute la régie
            à chaque bascule. Monté plus bas, il était détruit à chaque changement de
            chaîne : `destroy()` libère toutes les sources, donc `track.stop()` sur la
            webcam. Elle s'éteignait puis devait se rouvrir aussitôt, et un périphérique
            tout juste relâché refuse de se rouvrir — d'où la caméra locale perdue en
            passant d'une chaîne à l'autre.

            Il doit rester SOUS MediaStreamProvider : le provider du registre lit
            `useMediaStream()` pour les emplacements de partage d'écran. */}
        <SourceRegistryProvider>
          <RouterProvider router={router} />
        </SourceRegistryProvider>
      </MediaStreamProvider>
    </Provider>
  </React.StrictMode>
);
